import React, { useEffect, useState, useRef } from "react";
import Modal from "react-bootstrap/Modal";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import styled, { css } from "styled-components";
import Image from "../ui/Image";
import Loader from "../ui/Loader";
import SignatureCanvas from "react-signature-canvas";
import FloatingLabel from "react-bootstrap/FloatingLabel";
import Form from "react-bootstrap/Form";
import ModalUpdateError from "../modal/ModalUpdateError";
import { useSelector } from "react-redux";
import { parseExtraValues, mergeExtraValues } from "../../utils/extraValues";

const LANGUAGES = [
  { value: "en", label: "English" },
  { value: "es", label: "Español" },
  { value: "fr", label: "Français" },
  { value: "de", label: "Deutsch" },
  { value: "it", label: "Italiano" },
  { value: "pt", label: "Português" },
  { value: "ja", label: "日本語" },
  { value: "ko", label: "한국어" },
  { value: "zh", label: "中文" },
];

const COUNTRIES = [
  { value: "CANADA", label: "Canada" },
  { value: "USA", label: "United States" },
  { value: "MEXICO", label: "México" },
  { value: "UK", label: "United Kingdom" },
  { value: "AUSTRALIA", label: "Australia" },
  { value: "NEW ZEALAND", label: "New Zealand" },
  { value: "GERMANY", label: "Deutschland" },
  { value: "FRANCE", label: "France" },
  { value: "ITALY", label: "Italia" },
  { value: "SPAIN", label: "España" },
  { value: "JAPAN", label: "日本" },
  { value: "KOREA", label: "한국" },
];

const CA_PROVINCES = [
  { value: "AB", label: "Alberta" },
  { value: "BC", label: "British Columbia" },
  { value: "MB", label: "Manitoba" },
  { value: "NB", label: "New Brunswick" },
  { value: "NL", label: "Newfoundland and Labrador" },
  { value: "NS", label: "Nova Scotia" },
  { value: "NT", label: "Northwest Territories" },
  { value: "NU", label: "Nunavut" },
  { value: "ON", label: "Ontario" },
  { value: "PE", label: "Prince Edward Island" },
  { value: "QC", label: "Quebec" },
  { value: "SK", label: "Saskatchewan" },
  { value: "YT", label: "Yukon" },
];

const US_STATES = [
  ["AL", "Alabama"], ["AK", "Alaska"], ["AZ", "Arizona"], ["AR", "Arkansas"],
  ["CA", "California"], ["CO", "Colorado"], ["CT", "Connecticut"], ["DE", "Delaware"],
  ["FL", "Florida"], ["GA", "Georgia"], ["HI", "Hawaii"], ["ID", "Idaho"],
  ["IL", "Illinois"], ["IN", "Indiana"], ["IA", "Iowa"], ["KS", "Kansas"],
  ["KY", "Kentucky"], ["LA", "Louisiana"], ["ME", "Maine"], ["MD", "Maryland"],
  ["MA", "Massachusetts"], ["MI", "Michigan"], ["MN", "Minnesota"], ["MS", "Mississippi"],
  ["MO", "Missouri"], ["MT", "Montana"], ["NE", "Nebraska"], ["NV", "Nevada"],
  ["NH", "New Hampshire"], ["NJ", "New Jersey"], ["NM", "New Mexico"], ["NY", "New York"],
  ["NC", "North Carolina"], ["ND", "North Dakota"], ["OH", "Ohio"], ["OK", "Oklahoma"],
  ["OR", "Oregon"], ["PA", "Pennsylvania"], ["RI", "Rhode Island"], ["SC", "South Carolina"],
  ["SD", "South Dakota"], ["TN", "Tennessee"], ["TX", "Texas"], ["UT", "Utah"],
  ["VT", "Vermont"], ["VA", "Virginia"], ["WA", "Washington"], ["WV", "West Virginia"],
  ["WI", "Wisconsin"], ["WY", "Wyoming"],
].map(([value, label]) => ({ value, label }));

const REGIONS_BY_COUNTRY = {
  CANADA: CA_PROVINCES,
  USA: US_STATES,
};

const StretchStyle = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  flex-direction: column;
`;

const ImageLoader = styled.div`
  width: 50px;
  height: 50px;
`;

const ImageDiv = styled.div`
  padding: 0;
  width: 125px;
  height: 125px;
  border-radius: 50%;
  justify-content: center;
  align-items: center;
  margin: 20px;
  overflow: hidden;

  @media (min-width: 1024px) and (orientation: Portrait) {
    width: 220px;
    height: 220px;
  }
`;

const PhotoWrapper = styled.div`
  position: relative;
  display: inline-flex;
  cursor: ${(props) => (props.$readOnly ? "default" : "pointer")};
`;

const CameraBadge = styled.div`
  position: absolute;
  bottom: 22px;
  right: 22px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #00197b;
  border: 3px solid #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
  pointer-events: none;

  @media (min-width: 1024px) and (orientation: Portrait) {
    width: 52px;
    height: 52px;
    bottom: 26px;
    right: 26px;
  }
`;

const CameraIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
    <circle cx="12" cy="13" r="4" />
  </svg>
);

const Section = styled.div`
  background: var(--unnamed-color-ffffff) 0% 0% no-repeat padding-box;
  border: 1px solid var(--unnamed-color-d4d4d5);
  background: #ffffff 0% 0% no-repeat padding-box;
  opacity: 1;
  width: 150px;

  ${(props) =>
    props.primary &&
    css`
      width: auto;
    `}
  ${(props) =>
    props.big &&
    css`
      width: auto;
    `}
`;

const Button = styled.button`
  display: inline-block;
  margin: 0.5rem 0;
  width: 50px;
  height: 55px;
  background: ${(props) => (props.active === true ? "#00297A" : "#E0E1E2")};
  color: ${(props) => (props.active === true ? "#FFFFFF" : "#00000099")};
  border: 2px solid white;
  font: normal normal 600 12px/18px Poppins;
  font-weight: 700;
  text-align: center;
  padding: 0;
  vertical-align: bottom;
  text-transform: uppercase;

  ${(props) =>
    props.primary &&
    css`
      border-radius: 7px;
      width: 130px;
      font: normal normal 600 32px var(--unnamed-font-family-poppins);
      font-weight: 700;
      color: #ffffff;
      opacity: 1;
      letter-spacing: 0px;

      @media only screen and (max-width: 900px) and (orientation: portrait) {
        text-align: center;
        font: normal normal 600 32px/48px Poppins;
        width: 70px;
        padding: 0;
      }
      @media only screen and (max-width: 450px) and (orientation: portrait) {
        padding: 0;
        margin: 0;
        width: 68px;
      }
    `}
  ${(props) =>
    props.left &&
    css`
      border-radius: 8px 0px 0px 8px;
      margin: 0.5rem 0;
    `}
    ${(props) =>
    props.right &&
    css`
      border-radius: 0px 8px 8px 0px;
      margin: 0.5rem 0.01rem;
      width: 53px;
    `}
    ${(props) =>
    props.reduceSize &&
    css`
      font: var(--unnamed-font-style-normal) normal 600
        var(--unnamed-font-size-12) / 15px var(--unnamed-font-family-poppins);
      font: normal normal 600 11px/15px Poppins;
    `}
    
    &:hover {
    background: #00187e;
    color: #ffffff;
  }
`;

const InputDiv = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;

  @media (max-width: 1024px) and (orientation: Portrait) {
    flex-direction: column;
  }
`;

const ButtonAdd = styled.button`
  background: #00297a 0% 0% no-repeat padding-box;
  border-color: #00297a;
  border-radius: 10px;
  margin-top: 20px;
  opacity: 1;
  width: 150px;
  height: 48px;
  font-weight: 500;
  color: #ffffff;

  &:hover {
    background: #00187e;
    border-color: #00187e;
  }
  @media (max-width: 768px) {
    margin-top: 10px;
  }
`;

const nameStyle = {
  verticalAlign: "middle",
  display: "flex",
  textAlign: "center",
  justifyContent: "center",
  fontSize: "14px",
  fontWeight: "900",
  color: "#00197B",
  marginBottom: "10px",
};

const imageStyle = {
  width: "100%",
  height: "100%",
  borderRadius: "50%",
  imageRendering: "-webkit-optimize-contrast",
};

const FormStretchStyle = styled.form`
  display: flex;
  justify-content: center;
  align-items: stretch;
  width: -webkit-fill-available;
  flex-direction: "column",
  margin-top: 4px,
`;

const FormNonStretchStyle = styled.form`
  display: flex;
  justify-content: center;
  align-items: center;
  width: -webkit-fill-available;
  flex-direction: "column",
  margin-top: 4px,
`;

const formControlStyle = {
  marginTop: 4,
  marginBottom: 4,
  marginRight: 20,
  minHeight: 42,
};

const UpdateProfile = (props) => {
  const {
    showEditProfileModal,
    setShowEditProfileModal,
    bowler,
    setCustomer,
    woocommerceCheckUserExists,
    woocommerceUpdateCustomer,
    isReadOnly,
    image,
    savingPhoto,
    defaultImage,
    setPhotoShowModal,
    require_signature,
    require_email,
    require_phone,
    pluginSubscriptions,
    deleteCustomer,
    current_woocommerce_id,
  } = props;

  const [firstnameValue, setFirstnameValue] = useState(bowler.firstname);
  const [lastnameValue, setLastnameValue] = useState(bowler.lastname);
  const [emailValue, setEmailValue] = useState(bowler.email);
  const [telephoneValue, setTelephoneValue] = useState(bowler.telephone);
  const [addressValue, setAddressValue] = useState(bowler.address);
  const [cityValue, setCityValue] = useState(bowler.city);
  const [stateValue, setStateValue] = useState(bowler.state);
  const [countryValue, setCountryValue] = useState(bowler.country);
  const [postalCodeValue, setPostalCodeValue] = useState(bowler.postal_code);
  const [languageValue, setLanguageValue] = useState(bowler.preferred_lang);
  // #854: the bowler's home center. It lives in extra_values rather than a
  // column of its own -- same free-form JSON the POS link already uses -- so
  // it needs no migration and no audit mirror column.
  const [bowlingCenterValue, setBowlingCenterValue] = useState(
    parseExtraValues(bowler.extra_values).bowling_center || ""
  );
  // The pro shop's saved centers, offered as suggestions. Free text either
  // way: a bowler from out of town should not need a settings trip first.
  const bowlingCenters = useSelector(
    (state) =>
      state.settings.appSettings.bowlerPreferences?.bowling_centers || []
  );
  const [showAlert, setShowAlert] = useState(false);
  const [showAlertMessage, setShowAlertMessage] = useState("");
  const [showDelete, setShowDelete] = useState(false);
  const [imageURL, setImageURL] = useState(bowler.signature);
  const [hasError, setHasError] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);
  //Use the above to track whether to call woocommerce update or create user
  const { t } = useTranslation();
  const navigate = useNavigate();

  const sigCanvas = useRef({});

  const save = () =>
    setImageURL(sigCanvas.current.getCanvas().toDataURL("image/png"));

  const clear = () => sigCanvas.current.clear();

  const handleUpdateProfile = (e) => {
    e.preventDefault();
  };

  const isSavedCanvasEmpty = (canvas) => {
    const blank = document.createElement("canvas");

    blank.width = canvas.width;
    blank.height = canvas.height;

    return imageURL === blank.toDataURL();
  };

  const handleValues = (fieldName) => {
    //Add the woocommerce calls here for when adding or editing the profile.
    const values = getValuesObj();
    if (
      values.firstname === "" ||
      values.lastname === "" ||
      (require_email === true &&
        (values.email === "" || !validateEmail(values.email))) ||
      (require_phone === true && values.telephone === "")
    ) {
      setHasError(true);
      setShowAlert(true);
      setShowAlertMessage(t("Please fill in all required fields"));
      return;
    }
    if (
      require_signature === true &&
      (imageURL === "" || isSavedCanvasEmpty(sigCanvas.current.getCanvas()))
    ) {
      setHasError(true);
      setShowAlert(true);
      setShowAlertMessage(t("Please sign the consent form"));
      return;
    }
    if (pluginSubscriptions.indexOf("SPECTRE_POS") >= 0) {
      if (isNewUser) {
        // Route new-user saves through the search-and-match path too. Going
        // direct to create here used to skip the existing-customer search
        // and silently produce duplicates in WC whenever the proshop saved
        // a bowler whose name already lived as a WC customer (e.g. a
        // walk-in already linked from POS). The epic will search by name,
        // link to the match if it finds one, and only create when no
        // candidate exists. The chokepoint synthesizes email/billing.email
        // on the create branch when those fields are empty.
        woocommerceCheckUserExists({
          first_name: values.firstname,
          last_name: values.lastname,
          email: values.email,
          spectreCustomer: { ...bowler, ...values },
          nextAction: "create",
          customerData: {
            first_name: values.firstname,
            last_name: values.lastname,
            email: values.email,
            username: values.telephone,

            billing: {
              first_name: values.firstname,
              last_name: values.lastname,
              company: "",
              address_1: values.address,
              address_2: "",
              city: values.city,
              state: values.state,
              postcode: values.postal_code,
              country: values.country,
              email: values.email,
              phone: values.telephone,
            },
            shipping: {
              first_name: values.firstname,
              last_name: values.lastname,
              company: "",
              address_1: values.address,
              address_2: "",
              city: values.city,
              state: values.state,
              postcode: values.postal_code,
              country: values.country,
            },
          },
        });
      } else {
        woocommerceCheckUserExists({
          first_name: bowler.firstname,
          last_name: bowler.lastname,
          email: bowler.email,
          spectreCustomer: bowler,
          nextAction: "update",
          customerData: {
            first_name: values.firstname,
            last_name: values.lastname,
            email: values.email,
            username: bowler.telephone,

            billing: {
              first_name: values.firstname,
              last_name: values.lastname,
              address_1: values.address,
              city: values.city,
              state: values.state,
              postcode: values.postal_code,
              country: values.country,
              email: values.email,
              phone: values.telephone,
            },
            shipping: {
              first_name: values.firstname,
              last_name: values.lastname,
              address_1: values.address,
              city: values.city,
              state: values.state,
              postcode: values.postal_code,
              country: values.country,
            },
          },
        });
      }
    }
    setHasError(false);
    setCustomer({ bowler: { ...bowler, ...values } });
    setShowEditProfileModal(false);
  };

  const handlePhotoModalOpen = () => {
    setPhotoShowModal(true);
    setShowEditProfileModal(false);
  };

  const getValuesObj = () => {
    return {
      firstname: firstnameValue,
      lastname: lastnameValue,
      email: emailValue,
      telephone: telephoneValue,
      address: addressValue,
      city: cityValue,
      state: stateValue,
      postal_code: postalCodeValue,
      country: countryValue,
      preferred_lang: languageValue,
      signature: imageURL,
      extra_values: mergeExtraValues(bowler.extra_values, {
        bowling_center: bowlingCenterValue,
      }),
    };
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; // Basic email validation regex
    return emailRegex.test(email);
  };

  useEffect(() => {
    if (showEditProfileModal) {
      // Check if all required fields are blank
      const isBlank =
        !bowler.firstname &&
        !bowler.lastname &&
        (!require_email || !bowler.email) &&
        (!require_phone || !bowler.telephone) &&
        (!require_signature || !bowler.signature);

      setIsNewUser(isBlank); // Set the flag based on the check
    }
  }, [showEditProfileModal]);

  return (
    <div>
      {pluginSubscriptions.indexOf("BOWLER_PLUS") >= 0 && (
        <Modal
          show={showEditProfileModal}
          onHide={() => {
            console.log(validateEmail(bowler.email));
            const isInvalid =
              bowler.firstname === "" ||
              bowler.lastname === "" ||
              (require_email === true &&
                (bowler.email === "" || !validateEmail(bowler.email))) ||
              (require_phone === true && bowler.telephone === "") ||
              (require_signature === true &&
                (imageURL === "" ||
                  imageURL === document.createElement("canvas").toDataURL()));
            if (isInvalid) {
              setHasError(true);
              if (
                bowler.firstname === "" &&
                bowler.lastname === "" &&
                bowler.email === "" &&
                bowler.telephone === "" &&
                (imageURL === "" ||
                  imageURL === document.createElement("canvas").toDataURL()) &&
                bowler.address === "" &&
                bowler.city === "" &&
                bowler.state === "" &&
                bowler.country === "" &&
                bowler.postal_code === "" &&
                bowler.preferred_lang === ""
              ) {
                setShowDelete(true);
              }
            } else {
              setShowEditProfileModal(false);
            }
          }}
          size="xl"
          centered
          scrollable
        >
          <Modal.Header
            closeButton
            style={{
              backgroundColor: "#E6E6E6",
              borderBottom: "0 none",
            }}
          >
            <div style={{ marginTop: 10 }}>
              <span
                style={{
                  fontSize: 20,
                  fontWeight: "bold",
                  color: "#00197B",
                }}
              >
                {t("Edit Profile")}
              </span>
            </div>
          </Modal.Header>
          <Modal.Body className="modal-body-responsive">
            {" "}
            <div>
              <span style={nameStyle}>{t("Photo")}</span>
              {!savingPhoto && (
                <PhotoWrapper
                  $readOnly={isReadOnly}
                  onClick={() => {
                    !isReadOnly && handlePhotoModalOpen();
                  }}
                >
                  <ImageDiv>
                    <Image
                      style={imageStyle}
                      src={imageURL === null ? defaultImage : image}
                      fluid={"true"}
                      alt={""}
                      placeholder={<div />}
                      placeholderSrc={defaultImage}
                      wrapperProps={{
                        style: {
                          width: "100%",
                          height: "100%",
                          borderRadius: "50%",
                        },
                      }}
                    />
                  </ImageDiv>
                  {!isReadOnly && (
                    <CameraBadge>
                      <CameraIcon />
                    </CameraBadge>
                  )}
                </PhotoWrapper>
              )}
              {savingPhoto && (
                <ImageLoader
                  style={{
                    width: "100px",
                    height: "100px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Loader />
                </ImageLoader>
              )}
            </div>
            <FormStretchStyle onSubmit={handleUpdateProfile}>
              <StretchStyle>
                <InputDiv>
                  <FloatingLabel
                    controlId="floatingPassword"
                    label={t("form.placeholder.first_name") + "*"}
                    style={{
                      width: "100%",
                      marginRight: "10px",
                      marginTop: "10px",
                      color:
                        firstnameValue === "" && hasError ? "red" : "#00197b",
                    }}
                  >
                    <Form.Control
                      readOnly={isReadOnly}
                      id="floatingFirstName"
                      value={firstnameValue}
                      placeholder={t("form.placeholder.first_name")}
                      name="firstname"
                      onChange={(e) => setFirstnameValue(e.target.value)}
                    />
                  </FloatingLabel>
                  <FloatingLabel
                    controlId="floatingPassword"
                    label={t("form.placeholder.last_name") + "*"}
                    style={{
                      width: "100%",
                      marginRight: "10px",
                      marginTop: "10px",
                      color:
                        lastnameValue === "" && hasError ? "red" : "#00197b",
                    }}
                  >
                    <Form.Control
                      readOnly={isReadOnly}
                      id="floatingLastName"
                      value={lastnameValue}
                      placeholder={t("form.placeholder.last_name")}
                      name="lastname"
                      onChange={(e) => setLastnameValue(e.target.value)}
                    />
                  </FloatingLabel>
                  <FloatingLabel
                    controlId="floatingPassword"
                    label={
                      require_phone === true
                        ? t("form.placeholder.phone") + "*"
                        : t("form.placeholder.phone")
                    }
                    style={{
                      width: "100%",
                      marginRight: "10px",
                      marginTop: "10px",
                      color:
                        telephoneValue === "" &&
                        hasError &&
                        require_phone === true
                          ? "red"
                          : "#00197b",
                    }}
                  >
                    <Form.Control
                      readOnly={isReadOnly}
                      id="floatingTelephone"
                      value={telephoneValue}
                      placeholder={t("form.placeholder.phone")}
                      name="telephone"
                      onChange={(e) => setTelephoneValue(e.target.value)}
                    />
                  </FloatingLabel>
                </InputDiv>

                <InputDiv>
                  <FloatingLabel
                    controlId="floatingPassword"
                    label={t("form.placeholder.address")}
                    style={{
                      width: "100%",
                      marginRight: "10px",
                      marginTop: "10px",
                    }}
                  >
                    <Form.Control
                      readOnly={isReadOnly}
                      id="floatingAddress"
                      value={addressValue}
                      placeholder={t("form.placeholder.address")}
                      name="address"
                      onChange={(e) => setAddressValue(e.target.value)}
                    />
                  </FloatingLabel>
                  <FloatingLabel
                    controlId="floatingPassword"
                    label={t("form.placeholder.city")}
                    style={{
                      width: "100%",
                      marginRight: "10px",
                      marginTop: "10px",
                    }}
                  >
                    <Form.Control
                      readOnly={isReadOnly}
                      id="floatingCity"
                      value={cityValue}
                      placeholder={t("form.placeholder.city")}
                      name="city"
                      onChange={(e) => setCityValue(e.target.value)}
                    />
                  </FloatingLabel>
                  <FloatingLabel
                    controlId="floatingPassword"
                    label={t("form.placeholder.state")}
                    style={{
                      width: "100%",
                      marginRight: "10px",
                      marginTop: "10px",
                    }}
                  >
                    {REGIONS_BY_COUNTRY[countryValue] ? (
                      <Form.Select
                        disabled={isReadOnly}
                        id="floatingState"
                        value={stateValue}
                        name="state"
                        onChange={(e) => setStateValue(e.target.value)}
                      >
                        <option value="">—</option>
                        {REGIONS_BY_COUNTRY[countryValue].map((r) => (
                          <option key={r.value} value={r.value}>
                            {r.label}
                          </option>
                        ))}
                      </Form.Select>
                    ) : (
                      <Form.Control
                        readOnly={isReadOnly}
                        id="floatingState"
                        value={stateValue}
                        placeholder={t("form.placeholder.state")}
                        name="state"
                        onChange={(e) => setStateValue(e.target.value)}
                      />
                    )}
                  </FloatingLabel>
                </InputDiv>

                <InputDiv>
                  <FloatingLabel
                    controlId="floatingPassword"
                    label={t("form.placeholder.postalCode")}
                    style={{
                      width: "100%",
                      marginRight: "10px",
                      marginTop: "10px",
                    }}
                  >
                    <Form.Control
                      readOnly={isReadOnly}
                      id="floatingPostalCode"
                      value={postalCodeValue}
                      placeholder={t("form.placeholder.postalCode")}
                      name="postal_code"
                      onChange={(e) => setPostalCodeValue(e.target.value)}
                    />
                  </FloatingLabel>

                  <FloatingLabel
                    controlId="floatingPassword"
                    label={t("form.placeholder.country")}
                    style={{
                      width: "100%",
                      marginRight: "10px",
                      marginTop: "10px",
                    }}
                  >
                    <Form.Select
                      disabled={isReadOnly}
                      id="floatingCountry"
                      value={countryValue}
                      name="country"
                      onChange={(e) => {
                        setCountryValue(e.target.value);
                        // Reset province if it doesn't belong to new country
                        const regions = REGIONS_BY_COUNTRY[e.target.value];
                        if (
                          regions &&
                          !regions.find((r) => r.value === stateValue)
                        ) {
                          setStateValue("");
                        }
                      }}
                    >
                      <option value="">—</option>
                      {COUNTRIES.map((c) => (
                        <option key={c.value} value={c.value}>
                          {c.label}
                        </option>
                      ))}
                    </Form.Select>
                  </FloatingLabel>
                </InputDiv>

                <InputDiv>
                  <FloatingLabel
                    controlId="floatingPassword"
                    label={t("form.placeholder.language")}
                    style={{
                      width: "100%",
                      marginTop: "10px",
                      marginRight: "10px",
                      color: "#00197b",
                    }}
                  >
                    <Form.Select
                      disabled={isReadOnly}
                      id="language"
                      value={languageValue}
                      onChange={(e) => setLanguageValue(e.target.value)}
                      name="preffered_lang"
                    >
                      <option value="">—</option>
                      {LANGUAGES.map((l) => (
                        <option key={l.value} value={l.value}>
                          {l.label}
                        </option>
                      ))}
                    </Form.Select>
                  </FloatingLabel>
                  <FloatingLabel
                    controlId="floatingPassword"
                    label={
                      require_email === true
                        ? t("form.placeholder.email") + "*"
                        : t("form.placeholder.email")
                    }
                    style={{
                      width: "100%",
                      marginRight: "10px",
                      marginTop: "10px",
                      color:
                        emailValue === "" && hasError && require_email === true
                          ? "red"
                          : "#00197b",
                    }}
                  >
                    <Form.Control
                      readOnly={isReadOnly}
                      id="floatingEmail"
                      value={emailValue}
                      onChange={(e) => setEmailValue(e.target.value)}
                      placeholder={t("form.placeholder.email")}
                      name="email"
                    />
                  </FloatingLabel>
                </InputDiv>

                <InputDiv>
                  <FloatingLabel
                    controlId="floatingBowlingCenter"
                    label={t("form.placeholder.bowlingCenter")}
                    style={{
                      width: "100%",
                      marginRight: "10px",
                      marginTop: "10px",
                    }}
                  >
                    <Form.Control
                      readOnly={isReadOnly}
                      id="floatingBowlingCenter"
                      list="bowling-centers"
                      value={bowlingCenterValue}
                      placeholder={t("form.placeholder.bowlingCenter")}
                      name="bowling_center"
                      onChange={(e) => setBowlingCenterValue(e.target.value)}
                    />
                    <datalist id="bowling-centers">
                      {bowlingCenters.map((center) => (
                        <option key={center} value={center} />
                      ))}
                    </datalist>
                  </FloatingLabel>
                </InputDiv>

                <div className="form-floating" style={formControlStyle}>
                  <div className="modal-body-responsive">
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <span
                        style={{
                          verticalAlign: "middle",
                          display: "flex",
                          textAlign: "center",
                          justifyContent: "center",
                          fontSize: "14px",
                          fontWeight: "900",
                          color:
                            require_signature === true &&
                            (imageURL === "" ||
                              imageURL ===
                                document.createElement("canvas").toDataURL()) &&
                            hasError
                              ? "red"
                              : "#00197B",
                          marginBottom: "10px",
                        }}
                      >
                        {require_signature === true
                          ? t("Consent") + "*"
                          : t("Consent")}
                      </span>
                      <Section
                        primary
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          flexDirection: "column",
                        }}
                      >
                        <SignatureCanvas
                          ref={sigCanvas}
                          canvasProps={{
                            width: 300,
                            height: 100,
                            className: "sigCanvas",
                          }}
                        />
                      </Section>
                      <div>
                        <button onClick={save} style={{ margin: "10px" }}>
                          {t("Sign")}
                        </button>
                        <button onClick={clear} style={{ margin: "10px" }}>
                          {t("Clear")}
                        </button>
                      </div>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <span
                        style={{
                          verticalAlign: "middle",
                          display: "flex",
                          textAlign: "center",
                          justifyContent: "center",
                          fontSize: "14px",
                          fontWeight: "900",
                          color:
                            require_signature === true &&
                            (imageURL === "" ||
                              imageURL ===
                                document.createElement("canvas").toDataURL()) &&
                            hasError
                              ? "red"
                              : "#00197B",
                          marginBottom: "10px",
                        }}
                      >
                        {require_signature === true
                          ? t("Saved Signature") + "*"
                          : t("Saved Signature")}
                      </span>
                      <Image
                        src={
                          imageURL === ""
                            ? document.createElement("canvas").toDataURL()
                            : imageURL
                        }
                      ></Image>

                      <span style={nameStyle} marginTop="10px">
                        {t("Consent Description")}
                      </span>
                    </div>
                  </div>
                </div>
              </StretchStyle>
            </FormStretchStyle>
          </Modal.Body>
          <Modal.Footer
            style={{
              backgroundColor: "#E6E6E6",
              borderTop: "0 none",
              justifyContent: "flex-end",
            }}
          >
            <ButtonAdd type="submit" onClick={handleValues}>
              {t("Save")}
            </ButtonAdd>
          </Modal.Footer>
        </Modal>
      )}
      {pluginSubscriptions.indexOf("BOWLER_PLUS") < 0 && (
        <Modal
          show={showEditProfileModal}
          onHide={() => {
            const isInvalid =
              bowler.firstname === "" ||
              bowler.lastname === "" ||
              (require_email === true &&
                (bowler.email === "" || !validateEmail(bowler.email))) ||
              (require_phone === true && bowler.telephone === "");

            if (isInvalid) {
              setHasError(true);
              if (
                bowler.firstname === "" &&
                bowler.lastname === "" &&
                bowler.email === "" &&
                bowler.telephone === ""
              ) {
                setShowDelete(true);
              }
            } else {
              setShowEditProfileModal(false);
            }
          }}
          size="lg"
          centered
          scrollable
        >
          <Modal.Header
            closeButton
            style={{
              backgroundColor: "#E6E6E6",
              borderBottom: "0 none",
            }}
          >
            <div style={{ marginTop: 10 }}>
              <span
                style={{
                  fontSize: 20,
                  fontWeight: "bold",
                  color: "#00197B",
                }}
              >
                {t("Edit Profile")}
              </span>
            </div>
          </Modal.Header>
          <Modal.Body
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "#E6E6E6",
            }}
          >
            <div
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
            >
              <span style={nameStyle}>{t("Photo")}</span>
              {!savingPhoto && (
                <PhotoWrapper
                  $readOnly={isReadOnly}
                  onClick={() => {
                    !isReadOnly && setPhotoShowModal(true);
                  }}
                >
                  <ImageDiv>
                    <Image
                      style={{
                        width: "100%",
                        height: "100%",
                        borderRadius: "50%",
                        imageRendering: "-webkit-optimize-contrast",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                      src={imageURL === null ? defaultImage : image}
                      fluid={"true"}
                      alt={""}
                      placeholder={<div />}
                      placeholderSrc={defaultImage}
                      wrapperProps={{ style: { width: "100%", height: "100%" } }}
                    />
                  </ImageDiv>
                  {!isReadOnly && (
                    <CameraBadge>
                      <CameraIcon />
                    </CameraBadge>
                  )}
                </PhotoWrapper>
              )}
              {savingPhoto && (
                <ImageLoader
                  style={{
                    width: "100px",
                    height: "100px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Loader />
                </ImageLoader>
              )}
            </div>
            <FormNonStretchStyle onSubmit={handleUpdateProfile}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "100%",
                  flexDirection: "column",
                }}
              >
                <FloatingLabel
                  controlId="floatingPassword"
                  label={t("form.placeholder.first_name") + "*"}
                  className="float-label-non-plugin"
                  style={{
                    marginTop: "10px",
                    color:
                      firstnameValue === "" && hasError ? "red" : "#00197b",
                  }}
                >
                  <Form.Control
                    readOnly={isReadOnly}
                    id="floatingFirstName"
                    value={firstnameValue}
                    placeholder={t("form.placeholder.first_name")}
                    name="firstname"
                    onChange={(e) => setFirstnameValue(e.target.value)}
                  />
                </FloatingLabel>
                <FloatingLabel
                  controlId="floatingPassword"
                  label={t("form.placeholder.last_name") + "*"}
                  className="float-label-non-plugin"
                  style={{
                    marginTop: "10px",
                    color: lastnameValue === "" && hasError ? "red" : "#00197b",
                  }}
                >
                  <Form.Control
                    readOnly={isReadOnly}
                    id="floatingLastName"
                    value={lastnameValue}
                    placeholder={t("form.placeholder.last_name")}
                    name="lastname"
                    onChange={(e) => setLastnameValue(e.target.value)}
                  />
                </FloatingLabel>
                <FloatingLabel
                  controlId="floatingPassword"
                  label={
                    require_phone === true
                      ? t("form.placeholder.phone") + "*"
                      : t("form.placeholder.phone")
                  }
                  className="float-label-non-plugin"
                  style={{
                    marginTop: "10px",
                    color:
                      telephoneValue === "" &&
                      hasError &&
                      require_phone === true
                        ? "red"
                        : "#00197b",
                  }}
                >
                  <Form.Control
                    readOnly={isReadOnly}
                    id="floatingTelephone"
                    value={telephoneValue}
                    placeholder={t("form.placeholder.phone")}
                    name="telephone"
                    onChange={(e) => setTelephoneValue(e.target.value)}
                  />
                </FloatingLabel>
                <FloatingLabel
                  controlId="floatingPassword"
                  label={
                    require_email === true
                      ? t("form.placeholder.email") + "*"
                      : t("form.placeholder.email")
                  }
                  className="float-label-non-plugin"
                  style={{
                    marginTop: "10px",
                    color:
                      emailValue === "" && hasError && require_email
                        ? "red"
                        : "#00197b",
                  }}
                >
                  <Form.Control
                    readOnly={isReadOnly}
                    id="floatingEmail"
                    value={emailValue}
                    onChange={(e) => setEmailValue(e.target.value)}
                    placeholder={t("form.placeholder.email")}
                    name="email"
                  />
                </FloatingLabel>
              </div>
            </FormNonStretchStyle>
          </Modal.Body>
          <Modal.Footer
            style={{
              backgroundColor: "#E6E6E6",
              borderTop: "0 none",
              justifyContent: "center",
            }}
          >
            <ButtonAdd type="submit" onClick={handleValues}>
              {t("Save")}
            </ButtonAdd>
          </Modal.Footer>
        </Modal>
      )}
      <Modal
        show={showDelete}
        onHide={() => setShowDelete(false)}
        size="md"
        centered
      >
        <Modal.Header
          closeButton
          style={{
            backgroundColor: "#E6E6E6",
            borderBottom: "0 none",
          }}
        >
          <div style={{ marginTop: 10 }}>
            <span
              style={{
                fontSize: 20,
                fontWeight: "bold",
                color: "#00197B",
              }}
            >
              {t("Undo User")}
            </span>
          </div>
        </Modal.Header>
        <Modal.Body>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <span style={nameStyle}>
              {t("Are you sure you want to delete this user?")}
            </span>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            primary
            onClick={() => {
              setShowDelete(false);
            }}
            style={{
              marginRight: "10px",
              textTransform: "capitalize",
              width: "120px",
              fontSize: "20px",
            }}
          >
            {t("Cancel")}
          </Button>
          <Button
            primary
            style={{
              textTransform: "capitalize",
              width: "120px",
              fontSize: "20px",
            }}
            onClick={() => {
              console.log("Deleting customer with ID:", bowler.id);
              setShowEditProfileModal(false);
              setShowDelete(false);
              // Call the function to delete the user here
              deleteCustomer(bowler.id);
              navigate("/bowlers");
            }}
          >
            {t("Confirm")}
          </Button>
        </Modal.Footer>
      </Modal>
      <ModalUpdateError
        show={showAlert}
        onHide={() => setShowAlert(false)}
        confirmDescription={showAlertMessage}
      />
    </div>
  );
};

export default UpdateProfile;
